import {
  type SophiConfig,
  type IUserCart,
  type AddToCartEvent,
  type AddToFavoriteEvent,
  type Product,
  type ProductSummary,
} from "./types";
import { ERROR_MESSAGES } from "./constants/config";

export function validateConfig(config: SophiConfig): void {
  if (!config.apiKey || typeof config.apiKey !== "string") {
    throw new Error(ERROR_MESSAGES.MISSING_API_KEY);
  }

  if (config.userId && typeof config.userId !== "string") {
    throw new Error(ERROR_MESSAGES.MISSING_USER_ID);
  }

  if (!config.container) {
    throw new Error(ERROR_MESSAGES.MISSING_CONTAINER);
  }
}

export function validateUserCart(cart: IUserCart): void {
  if (!cart || typeof cart !== "object") {
    throw new Error("[Sophi] updateUserCart: cart must be a non-null object");
  }

  if (!cart.cartId || typeof cart.cartId !== "string") {
    throw new Error("[Sophi] updateUserCart: cart.cartId is required and must be a string");
  }

  if (!Array.isArray(cart.items)) {
    throw new Error("[Sophi] updateUserCart: cart.items must be an array");
  }

  cart.items.forEach((item, index) => {
    if (!item || typeof item !== "object") {
      throw new Error(`[Sophi] updateUserCart: cart.items[${index}] must be a non-null object`);
    }

    if (!item.productId || typeof item.productId !== "string") {
      throw new Error(`[Sophi] updateUserCart: cart.items[${index}].productId is required and must be a string`);
    }

    if (!item.variantId || typeof item.variantId !== "string") {
      throw new Error(`[Sophi] updateUserCart: cart.items[${index}].variantId is required and must be a string`);
    }

    if (item.quantity !== undefined && (typeof item.quantity !== "number" || item.quantity < 0)) {
      throw new Error(`[Sophi] updateUserCart: cart.items[${index}].quantity must be a non-negative number`);
    }
  });
}

export function isValidAddToCartMessage(data: unknown): data is AddToCartEvent {
  if (!data || typeof data !== "object") {
    return false;
  }

  const event = data as Record<string, unknown>;

  // Check products array exists and is an array
  if (!Array.isArray(event.products)) {
    return false;
  }

  // Validate each product in the array
  return event.products.every((product) => {
    if (!product || typeof product !== "object") {
      return false;
    }
    const prod = product as Record<string, unknown>;

    // productId is required and must be a string
    if (typeof prod.productId !== "string") {
      return false;
    }

    // variantId is optional, but if present must be a string
    if (prod.variantId !== undefined && typeof prod.variantId !== "string") {
      return false;
    }

    return true;
  });
}

export function parseAddToFavoriteMessage(data: unknown): AddToFavoriteEvent | null {
  if (!data || typeof data !== "object") {
    console.warn("[Sophi] add_to_favorite: invalid message data — ignoring");
    return null;
  }

  const payload = data as Record<string, unknown>;
  const product = payload.product;

  if (!product || typeof product !== "object") {
    console.warn("[Sophi] add_to_favorite: data.product is missing or invalid — ignoring");
    return null;
  }

  if (typeof payload.isFavorite !== "boolean") {
    console.warn("[Sophi] add_to_favorite: isFavorite must be a boolean — ignoring");
    return null;
  }

  const prod = product as Record<string, unknown>;
  const externalId = prod.externalId;
  const id = prod.id;
  const productId = prod.productId;
  const rawIdentifier =
    typeof externalId === "string" && externalId.length > 0
      ? externalId
      : id != null
        ? String(id)
        : typeof productId === "string" && productId.length > 0
          ? productId
          : null;

  if (rawIdentifier == null) {
    console.warn(
      "[Sophi] add_to_favorite: product has no externalId, id, or productId — ignoring",
    );
    return null;
  }

  return {
    product: product as Product | ProductSummary,
    isFavorite: payload.isFavorite,
    productIdentifier: rawIdentifier,
  };
}
